import { useEffect, useRef } from 'react';
import { useSpring, animated } from 'react-spring';
import PropTypes from 'prop-types';
import _ from 'lodash';
import Image from './image.component';

function debounce(func, wait = 5, immediate = true) {
    let timeout;
    return function delay(...args) {
        const context = this;
        const later = function delayLater() {
            timeout = null;
            if (!immediate) func.apply(context, args);
        };
        const callNow = immediate && !timeout;
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
        if (callNow) func.apply(context, args);
    };
}

const calc = (offset, speed) => {
    const interp = offset.to((o) => `translateY(${o * speed}px)`);
    return interp;
};

const Shapes = (props) => {
    const {
        containerMargins,
        speed,
        isCircle,
        isSmallDark,
        isLargeDark,
        isDark,
        isLight,
        isOpposite,
        isDots,
        isSquare,
        smallRadius,
        largeRadius,
        isPair,
        isSingle,
        distanceBetween,
        largeMargins,
        smallMargins,
        degrees,
        zIndex,
    } = props;

    const [{ offset }, api] = useSpring(() => ({ offset: 0 }));
    const ref = useRef();
    const handleScroll = () => {
        if (_.get(ref, 'current')) {
            const posY = ref.current.getBoundingClientRect().top;
            const offsetY = window.pageYOffset - posY;
            api.start({ offset: offsetY });
        }
    };

    useEffect(() => {
        window.addEventListener('scroll', debounce(handleScroll), true);
        return () => {
            window.removeEventListener('scroll', debounce(handleScroll), true);
        };
    }, []);

    const renderDotsContainer = () => {
        return (
            <>
                <div ref={ref} style={{
                    position: 'absolute',
                    display: 'block',
                    zIndex: zIndex ? `${zIndex}` : '-1',
                    left: containerMargins.left,
                    top: containerMargins.top,
                }}>
                    <animated.div style={{ transform: calc(offset, speed) }}>
                        {renderDotsShapes()}
                    </animated.div>                    
                </div>
            </>
        );
    };

    const renderDotsShapes = () => {
        if (isDots) {
            if (isLight) {
                return (
                    <img
                        src="/assets/images/dotted_background_light.png"
                        alt="Dotted Background Light"
                        width={smallMargins.width}
                        height={smallMargins.height}
                        offset={offset}
                        speed={speed}
                    />
                );
            }
            if (isSquare) {
                return (
                    <img
                        src="/assets/images/dotted_background_square.png"
                        alt="Dotted Background Square"
                        width={smallMargins.width}
                        height={smallMargins.height}
                    />
                );
            }

            return (
                <img
                    src="/assets/images/dotted_background.png"
                    alt="Dotted Background Dark"
                    width={smallMargins.width}
                    height={smallMargins.height}
                />
            );
        }
        return null;
    };

    const renderOuterContainer = () => {
        if (isPair) {
            return (
                <>
                    <div ref={ref} style={{
                        position: 'absolute',
                        display: 'block',
                        left: containerMargins.left,
                        top: containerMargins.top,
                        zIndex: zIndex ? `${zIndex}` : '-1',
                    }}>
                         <animated.div style={{ transform: calc(offset, speed) }}>
                            <div style={{ transform: degrees ? `rotate(${degrees})` : null }}>
                                {renderInnerContainers()}
                            </div>
                          </animated.div>  
                    </div>
                </>
            );
        }

        if (isSingle) {
            return (
                <>
                    <div ref={ref} style={{
                        position: 'absolute',
                        display: 'block',
                        left: containerMargins.left,
                        top: containerMargins.top,
                        zIndex: zIndex ? `${zIndex}` : '-1',
                    }}>
                         <animated.div style={{ transform: calc(offset, speed) }}>
                            <div style={{ transform: degrees ? `rotate(${degrees})` : null }}>
                                {renderInnerContainer()}
                            </div>
                          </animated.div>  
                    </div>
                </>
            );
        }

        return null;
    };

    const renderInnerContainer = () => {
        return (
            <>
                <div>{renderShape(isDark || isLargeDark, true)}</div>
            </>
        );
    };

    const renderInnerContainers = () => {
        if (isOpposite) {
            return (
                <>
                    <div
                        style={{
                            display: isCircle ? 'block' : 'flex',
                            float: isOpposite ? 'left' : 'right',
                            marginRight: distanceBetween.width,
                            marginTop: distanceBetween.height,
                            ...(isOpposite && {
                            marginLeft: distanceBetween.width,
                            marginBottom: distanceBetween.height,
                        }),
                    }}
                    >
                        {renderShape(isDark || isLargeDark, true)}
                    </div>

                    <div style={{
                        display: isCircle ? 'block' : 'flex',
                        float: isCircle ? 'right' : null,
                        justifyContent: 'center',
                        alignContent: 'center',
                    }}>
                        {renderShape(isDark || isSmallDark)}
                    </div>  

                </>
            );
        }
        return (
            <>
                <div style={{
                    display: isCircle ? 'block' : 'flex',
                    float: isCircle ? 'right' : null,
                    justifyContent: 'center',
                    alignContent: 'center',
                 }}>
                    {renderShape(isDark || isSmallDark)}
                </div>    

                <div
                    style={{
                        display: isCircle ? 'block' : 'flex',
                        float: isOpposite ? 'left' : 'right',
                        marginRight: distanceBetween.width,
                        marginTop: distanceBetween.height,
                        ...(isOpposite && {
                        marginLeft: distanceBetween.width,
                        marginBottom: distanceBetween.height,
                    }),
                }}
                >
                    {renderShape(isDark || isLargeDark, true)}
                </div>

            </>
        );
    };

    const renderShape = (isShapeDark, isLarge) => {
        let radius;
        let height;
        let width;

        if (!isLarge) {
            if (isCircle) {
                radius = smallRadius;
            } else {
                height = smallMargins.height;
                width = smallMargins.width;
            }
        } else if (isCircle) {
            radius = largeRadius;
        } else {
            height = largeMargins.height;
            width = largeMargins.width;
        }

        if (isShapeDark && isCircle) {
            return (
                <Image
                    src="/assets/images/full_circle_dark.png"
                    alt="Dark Circle"
                    width={radius}
                    height={radius}
                />
            );
        }
        if (!isShapeDark && isCircle) {
            return (
                <img
                    src="/assets/images/full_circle_light.png"
                    alt="Light Circle"
                    width={radius}
                    height={radius}
                />
            );
        }
        if (isShapeDark && !isCircle) {
            return (
                <Image
                    src="/assets/images/half_circle_dark.png"
                    alt="Half Circle Dark"
                    width={width}
                    height={height}
                />
            );
        }
        if (!isShapeDark && !isCircle) {
            return (
                <Image
                    src="/assets/images/half_circle_light.png"
                    alt="Half Circle Light"
                    width={width}
                    height={height}
                    priority
                />
            );
        }
        return null;
    };

    return (
        <>
            {renderDotsContainer()}
            {renderOuterContainer()}
        </>
    );
};

export default Shapes;

Shapes.propTypes = {
    containerMargins: PropTypes.object,
    speed: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    isCircle: PropTypes.bool,
    isSmallDark: PropTypes.bool,
    isLargeDark: PropTypes.bool,
    isDark: PropTypes.bool,
    isLight: PropTypes.bool,
    isOpposite: PropTypes.bool,
    isDots: PropTypes.bool,
    isSquare: PropTypes.bool,
    smallRadius: PropTypes.number,
    largeRadius: PropTypes.number,
    smallMargins: PropTypes.object,
    largeMargins: PropTypes.object,
    isPair: PropTypes.bool,
    isSingle: PropTypes.bool,
    distanceBetween: PropTypes.object,
    degrees: PropTypes.string,
    zIndex: PropTypes.number,
};

